// src/components/Purchase-orders/POCreateModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePOs } from "../../contexts/POContext";

const LS_DRAFT = "mudoDraftPO";

const normalizeUrl = (u) => {
  if (!u) return "";
  let s = String(u).trim();
  if (!s) return "";
  if (typeof u === "object" && u.url) s = String(u.url).trim();
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("www.")) return `https://${s}`;
  if (/^[a-z0-9.-]+\.[a-z]{2,}([/:].*)?$/i.test(s)) return `https://${s}`;
  return s;
};

export default function POCreateModal({ lowItems = [], onClose, onSaved }) {
  const { addOrder } = usePOs?.() || {};

  const rows = useMemo(() => (Array.isArray(lowItems) ? lowItems : []), [lowItems]);

  // Prefill qtys, supplier
  const [qtys, setQtys] = useState(() =>
    Object.fromEntries(rows.map((m) => [m.id, Number(m.suggestedQty ?? m.qty ?? 0) || 0]))
  );
  const [supplier, setSupplier] = useState(() => (rows.find((r) => (r.supplier || "").trim())?.supplier || ""));
  const [note, setNote] = useState("");

  const dialogRef = useRef(null);

  const hasAnyQty = useMemo(() => rows.some((m) => Number(qtys[m.id] || 0) > 0), [rows, qtys]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleSave();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, qtys, supplier, note]);

  const handleQtyChange = (id, v) => setQtys((q) => ({ ...q, [id]: v }));

  const handleSave = () => {
    const items = rows
      .map((m) => {
        const qty = Number(qtys[m.id] || 0);
        if (!qty || qty <= 0) return null;
        return {
          id: m.id,
          materialId: m.materialId || m.id,
          name: m.name,
          sku: m.sku || "",
          supplier: (m.supplier || supplier || "").trim(),
          website: normalizeUrl(m.website || ""),
          image: m.image || null,
          unit: m.unit || "unit",
          unitCost: Number(m.unitCost || 0),
          qty,
          category: m.category || "",
        };
      })
      .filter(Boolean);

    if (!items.length) {
      onClose?.();
      return;
    }

    const po = {
      id: `PO-${Date.now()}`,
      supplier: supplier || "",
      status: "Draft",
      createdAt: Date.now(),
      note: note.trim() || undefined,
      items,
    };

    if (typeof addOrder === "function") addOrder(po);

    try { localStorage.removeItem(LS_DRAFT); } catch {}
    onSaved?.();
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="po-modal-title"
      ref={dialogRef}
    >
      <div className="bg-white dark:bg-zinc-900 text-black dark:text-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-lg">
        {/* Header */}
        <div className="px-5 py-4 border-b border-black/10 dark:border-white/10 flex items-center justify-between">
          <h2 id="po-modal-title" className="text-lg font-semibold">Create Purchase Order</h2>
          <button
            onClick={onClose}
            className="text-xl leading-none px-2 py-1 rounded hover:bg-black/5 dark:hover:bg-white/10"
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Supplier + Note */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="md:col-span-1">
              <div className="text-[11px] opacity-70 mb-1">Supplier (optional)</div>
              <input
                type="text"
                value={supplier}
                onChange={(e) => setSupplier(e.target.value)}
                className="w-full p-2 rounded border border-black/10 dark:border-white/10 bg-white dark:bg-zinc-800"
                placeholder="e.g., Clayworks"
              />
            </label>
            <label className="md:col-span-2">
              <div className="text-[11px] opacity-70 mb-1">Note (optional)</div>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full p-2 rounded border border-black/10 dark:border-white/10 bg-white dark:bg-zinc-800"
                placeholder="e.g., Needed for next workshop"
              />
            </label>
          </div>

          {/* Lines */}
          {rows.length === 0 ? (
            <div className="text-sm opacity-70">No low-stock items were passed into this modal.</div>
          ) : (
            <div className="overflow-x-auto border border-black/10 dark:border-white/10 rounded">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left opacity-70">
                    <th className="py-2 px-3">Item</th>
                    <th className="py-2 px-3">SKU</th>
                    <th className="py-2 px-3 text-right">Unit</th>
                    <th className="py-2 px-3 text-right">Unit Cost</th>
                    <th className="py-2 px-3 text-right">Stock</th>
                    <th className="py-2 px-3 text-right">Suggest</th>
                    <th className="py-2 px-3 text-right">Qty to Order</th>
                    <th className="py-2 px-3 text-right">Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => {
                    const qty = Number(qtys[m.id] ?? m.suggestedQty ?? 0) || 0;
                    const unitCost = Number(m.unitCost || 0);
                    const line = unitCost * qty;
                    return (
                      <tr key={m.id} className="border-t border-black/5 dark:border-white/5">
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2">
                            {m.image ? (
                              <img
                                src={m.image}
                                alt={m.name}
                                className="h-9 w-9 rounded object-cover border border-black/10 dark:border-white/10"
                              />
                            ) : (
                              <div className="h-9 w-9 rounded bg-black/10 dark:bg-white/10" />
                            )}
                            <div className="min-w-0">
                              <div className="font-medium truncate">{m.name}</div>
                              <div className="text-[11px] opacity-70 truncate">{m.category || "—"}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-2 px-3">{m.sku || "—"}</td>
                        <td className="py-2 px-3 text-right">{m.unit || "unit"}</td>
                        <td className="py-2 px-3 text-right">${unitCost.toFixed(4)}</td>
                        <td className="py-2 px-3 text-right">{m.stock ?? "—"}</td>
                        <td className="py-2 px-3 text-right">{m.suggestedQty ?? "—"}</td>
                        <td className="py-2 px-3 text-right">
                          <input
                            type="number"
                            min="0"
                            className="w-24 p-1 rounded bg-white dark:bg-zinc-800 border border-black/10 dark:border-white/10 text-right"
                            value={qtys[m.id] ?? m.suggestedQty ?? ""}
                            onChange={(e) => handleQtyChange(m.id, e.target.value)}
                          />
                        </td>
                        <td className="py-2 px-3 text-right">${line.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-black/10 dark:border-white/10 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!hasAnyQty}
            className={`px-4 py-2 rounded text-white font-medium ${
              hasAnyQty ? "bg-amber-600 hover:bg-amber-700" : "bg-amber-900/50 cursor-not-allowed"
            }`}
            title={hasAnyQty ? "Save purchase order" : "Enter at least one quantity"}
          >
            Save PO
          </button>
        </div>
      </div>
    </div>
  );
}
